import { TOUR_START, TOOLBAR_DEFAULTS, GROUP_DEFAULTS, MANAGEMENT_DEFAULTS, TOUR_END } from '../../constants/project/projects-tour';
import { StepIdsEnum } from '../../constants/project/projects-tour-enums';
import { type TourStep } from '../../types/project-tour.types';
import { windowIsExtraLarge } from '../breakpoints/breakpoint.helpers';

export const getTourBoxPosition = (
	rect: DOMRect,
	verticalPosition: 'top' | 'bottom',
	topOffset: number,
	leftOffset: number,
	scrollTop: number,
	scrollLeft: number
): React.CSSProperties => {
	return {
		top: rect[verticalPosition] + scrollTop + topOffset,
		left: rect.left + scrollLeft + leftOffset
	};
};

export const getHighlightStyles = (
	currentStepId: StepIdsEnum,
	targetRect: DOMRect,
	scrollTop: number,
	scrollLeft: number
): React.CSSProperties => {
	const isOnCopyMoveStep = currentStepId === StepIdsEnum.COPY_MOVE;
	const isOnCompareStep = currentStepId === StepIdsEnum.COMPARE;
	const isOnToolbarButtonStep = isToolbarButtonStep(currentStepId);
	const isProductActionButton = isProductActionButtonStep(currentStepId);

	const padding = isProductActionButton ? 6 : isOnToolbarButtonStep ? 1 : 0;
	const topPadding = isProductActionButton ? 6 : 0;
	const copyMoveStepWidth = !windowIsExtraLarge() ? 225 : 182;

	let height: number;
	if (isOnCopyMoveStep && !windowIsExtraLarge()) {
		height = 86;
	} else if (isOnCompareStep && !windowIsExtraLarge()) {
		height = 42;
	} else if (isOnCopyMoveStep || isOnToolbarButtonStep) {
		height = 29;
	} else if (isProductActionButton) {
		height = targetRect.width + padding * 2;
	} else {
		height = targetRect.height + padding * 2;
	}

	return {
		top: targetRect.top - topPadding + scrollTop,
		left: targetRect.left - padding + scrollLeft,
		width: isOnCopyMoveStep ? copyMoveStepWidth : targetRect.width + padding * 2,
		height
	};
};

const {
	COPY_MOVE,
	COMPARE,
	ADD_TO_CART,
	NEW_GROUP,
	GROUP,
	SEARCH_FROM_PROJECTS,
	EDIT_PRODUCT,
	COMMENT,
	ORDERS_RETURNS,
	COMMUNICATION,
	PORTFOLIO
} = StepIdsEnum;

const isToolbarButtonStep = (id: StepIdsEnum | null): boolean => id !== null && [COMPARE, ADD_TO_CART, NEW_GROUP].includes(id);

export const isManagementStep = (id: StepIdsEnum | null): boolean => id !== null && [ORDERS_RETURNS, COMMUNICATION, PORTFOLIO].includes(id);

export const isGroupStep = (id: StepIdsEnum | null): boolean =>
	id !== null && [GROUP, SEARCH_FROM_PROJECTS, EDIT_PRODUCT, COMMENT].includes(id);

export const isToolbarStep = (id: StepIdsEnum | null): boolean => id !== null && [COPY_MOVE, COMPARE, ADD_TO_CART, NEW_GROUP].includes(id);

export const isMobileToolbarStep = (id: StepIdsEnum | null): boolean => id !== null && [COPY_MOVE, COMPARE].includes(id);

export const isProductActionButtonStep = (id: StepIdsEnum | null): boolean => id !== null && [EDIT_PRODUCT, COMMENT].includes(id);

export type TourFeatureFlags = {
	isSharingEnabled: boolean;
};

export const getTourSteps = ({ isSharingEnabled }: TourFeatureFlags): TourStep[][] => [
	[
		{
			id: TOUR_START,
			title: 'Projects Tour',
			description: 'See what features we have to help you organize and manage your projects',
			leftOffset: -8,
			verticalPosition: 'bottom'
		},
		{
			...TOOLBAR_DEFAULTS,
			id: COPY_MOVE,
			description: 'Copy and Move selected products and/or groups within this project or to other projects',
			mobileTopOffset: isSharingEnabled ? -130 : -32
		},
		{
			...TOOLBAR_DEFAULTS,
			id: COMPARE,
			description: 'Compare 2-4 selected products to see the differences and similarities between them',
			mobileTopOffset: isSharingEnabled ? -90 : 0
		},
		{
			...TOOLBAR_DEFAULTS,
			id: ADD_TO_CART,
			description: 'Add all selected products to your shopping cart'
		},
		{
			...TOOLBAR_DEFAULTS,
			id: NEW_GROUP,
			description: 'Create New Group(s) to further organize your project'
		}
	],
	[
		{
			...GROUP_DEFAULTS,
			id: GROUP,
			description: 'A group helps you organize and categorize the products in your project',
			topOffset: 60,
			mobileTopOffset: 250
		},
		{
			...GROUP_DEFAULTS,
			id: SEARCH_FROM_PROJECTS,
			description: 'You can search our product catalog to add to your group directly from this page',
			topOffset: 176,
			mobileTopOffset: 415
		},
		{
			...GROUP_DEFAULTS,
			id: EDIT_PRODUCT,
			description: 'You can edit the product to adjust quantity, finish, and see related products',
			topOffset: 192,
			mobileTopOffset: 325
		},
		{
			...GROUP_DEFAULTS,
			id: COMMENT,
			description: 'Leave comments on products to streamline collaboration and communication within your project',
			topOffset: 192,
			mobileTopOffset: 325
		}
	],
	[
		{
			...MANAGEMENT_DEFAULTS,
			id: ORDERS_RETURNS,
			description: 'View all orders, returns and their status within your project',
			mobileDescription: 'Open the menu to view all orders, returns and their status within your project',
			mobileTopOffset: 193
		},
		{
			...MANAGEMENT_DEFAULTS,
			id: COMMUNICATION,
			description: "Invite others to collaborate on your project, access project comments, and see what's changed with notifications",
			mobileTopOffset: 347
		},
		{
			...MANAGEMENT_DEFAULTS,
			id: PORTFOLIO,
			description: 'Export spec sheets or a pdf presentation portfolio of your project',
			mobileTopOffset: 210
		},
		{
			id: TOUR_END,
			title: 'Try it out!',
			description: 'Organize your next project with Projects',
			leftOffset: -289,
			verticalPosition: 'top'
		}
	]
];
